import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null=checking, false=guest, object=user
    const [ready, setReady] = useState(false);

    const refresh = useCallback(async () => {
        const token = localStorage.getItem("perx_token");
        if (!token) {
            setUser(false);
            setReady(true);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            localStorage.removeItem("perx_token");
            setUser(false);
        } finally {
            setReady(true);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem("perx_token", data.token);
        setUser(data.user);
        return data.user;
    };

    const signup = async (name, email, password) => {
        const { data } = await api.post("/auth/signup", { name, email, password });
        localStorage.setItem("perx_token", data.token);
        setUser(data.user);
        return data.user;
    };

    const logout = () => {
        localStorage.removeItem("perx_token");
        setUser(false);
    };

    // Multi-role: flip the caller's active portal (persisted server-side).
    // Only succeeds when they already hold the target role.
    const switchRole = async (role) => {
        const { data } = await api.post("/auth/active-role", { role });
        setUser(data);
        return data;
    };

    return (
        <AuthContext.Provider
            value={useMemo(
                () => ({ user, ready, refresh, login, signup, logout, switchRole }),
                // login/signup/logout/switchRole are re-declared each render but
                // stable in behaviour; consumers only need to notice changes to
                // user / ready / refresh (which is itself memoised).
                [user, ready, refresh]
            )}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}

export { formatApiError };
